'use client';

import {
  BatchMode,
  ErrorPolicy,
  FileFormat,
  IngestionSourceType,
  KafkaKeyHandling,
  KafkaMessageFormat,
  KafkaSecurityProtocol,
  KafkaStartingOffset,
  PartitionFormat,
  PartitionSegmentType,
  PartitionType,
  SchemaEvolutionMode,
  SchemaRegistryAuthType,
  type BucketPathPartitionSpec,
  type GcsSourceProperties,
  type IngestionPipeline,
  type KafkaSourceProperties,
  type S3SourceProperties,
  type UpdateIngestionPipelineInput,
} from '@repo/api-gw-sdk';

import { Wizard } from '@/components/wizard/Wizard';
import { useDAL } from '@/contexts/useDAL';
import { useWorkspace } from '@/contexts/useWorkspace';

import { IngestionPipelineCreationFlow } from './IngestionPipelineCreationFlow';
import {
  mapSegments,
  type IngestionPipelineWizardState,
} from './IngestionPipelineWizardState';
import { buildKafkaSourceProperties, getStepperLabels } from './kafkaHelpers';

interface UpdateIngestionPipelineWizardPanelProps {
  workspaceId: string;
  pipeline: IngestionPipeline;
  onAbort: () => void;
  onUpdated: () => void;
}

function pipelineToWizardState(
  pipeline: IngestionPipeline
): IngestionPipelineWizardState {
  const s3 = pipeline.s3SourceProperties;
  const gcs = pipeline.gcsSourceProperties;
  const kafka = pipeline.kafkaSourceProperties;
  const spec = s3?.bucketPathPartitionSpec ?? gcs?.bucketPathPartitionSpec;

  return {
    name: pipeline.name,
    namespace: pipeline.namespace,
    tableName: pipeline.tableName,
    sourceType: pipeline.sourceType,
    s3Bucket: s3?.bucket ?? '',
    s3Region: s3?.region ?? '',
    s3OwnerAccountId: s3?.ownerAccountId ?? '',
    gcsBucket: gcs?.bucket ?? '',
    gcsProjectId: gcs?.projectId ?? '',
    gcsLocation: gcs?.location ?? '',
    fileFormat:
      s3?.fileFormat ?? gcs?.fileFormat ?? FileFormat.FILE_FORMAT_JSON,

    // Kafka fields
    kafkaBootstrapServers: kafka?.source?.bootstrapServers?.join(', ') ?? '',
    kafkaTopic: kafka?.source?.topic ?? '',
    kafkaConsumerGroupId: kafka?.source?.consumerGroupId ?? '',
    kafkaStartingOffset:
      kafka?.source?.startingOffset ??
      KafkaStartingOffset.KAFKA_STARTING_OFFSET_EARLIEST,
    kafkaMessageFormat:
      kafka?.source?.messageFormat ??
      KafkaMessageFormat.KAFKA_MESSAGE_FORMAT_JSON,
    kafkaKeyHandling:
      kafka?.source?.keyHandling ?? KafkaKeyHandling.KAFKA_KEY_HANDLING_IGNORE,
    kafkaSecurityProtocol:
      kafka?.security?.securityProtocol ??
      KafkaSecurityProtocol.KAFKA_SECURITY_PROTOCOL_PLAINTEXT,
    kafkaSaslMechanism: kafka?.security?.saslMechanism ?? '',
    kafkaCredentialsSecretArn: kafka?.security?.credentialsSecretArn ?? '',
    kafkaTlsSecretArn: kafka?.security?.tlsSecretArn ?? '',
    kafkaSkipTlsVerification: kafka?.security?.skipTlsVerification ?? false,
    kafkaSchemaRegistryEnabled: !!kafka?.schemaRegistry?.url,
    kafkaSchemaRegistryUrl: kafka?.schemaRegistry?.url ?? '',
    kafkaSchemaRegistryAuthType:
      kafka?.schemaRegistry?.auth?.type ??
      SchemaRegistryAuthType.SCHEMA_REGISTRY_AUTH_TYPE_NONE,
    kafkaSchemaRegistryCredentialsSecretArn:
      kafka?.schemaRegistry?.auth?.credentialsSecretArn ?? '',
    kafkaSchemaRegistryTlsSecretArn:
      kafka?.schemaRegistry?.auth?.tlsSecretArn ?? '',
    kafkaSubjectNamingStrategy:
      kafka?.schemaRegistry?.subjectNamingStrategy ?? '',
    kafkaSchemaEvolutionMode:
      kafka?.schemaRegistry?.evolutionMode ??
      SchemaEvolutionMode.SCHEMA_EVOLUTION_MODE_BACKWARD_COMPATIBLE,
    kafkaPartitioningColumns: kafka?.partitioning?.strategy?.length
      ? kafka.partitioning.strategy
      : [''],
    kafkaBatchMode: kafka?.batch?.mode ?? BatchMode.BATCH_MODE_MICRO_BATCH,
    kafkaMaxRecordsPerBatch: kafka?.batch?.maxRecordsPerBatch?.toString() ?? '',
    kafkaMaxBatchIntervalSeconds:
      kafka?.batch?.maxBatchIntervalSeconds?.toString() ?? '',
    kafkaErrorPolicy:
      kafka?.errorHandling?.onError ?? ErrorPolicy.ERROR_POLICY_FAIL,
    kafkaMaxRetries: kafka?.errorHandling?.maxRetries?.toString() ?? '3',

    partitionSpec: {
      separator: spec?.separator ?? '/',
      segments: (spec?.segments ?? []).map((seg, i) => ({
        id: `seg-${i}`,
        segmentType: seg.segmentType,
        segmentStringValue: seg.segmentStringValue ?? '',
        segmentPartitionName: seg.segmentPartitionName ?? '',
        segmentPartitionType:
          seg.segmentPartitionType ??
          PartitionSegmentType.PARTITION_SEGMENT_TYPE_SECONDARY,
        segmentPartitionFormat:
          seg.segmentPartitionFormat ?? PartitionFormat.PARTITION_FORMAT_HIVE,
      })),
    },
    partitionBuilderMode: 'build',
    samplePath: '',
    existingPipelineNames: new Set<string>(),
    isUpdateMode: true,
  };
}

export function UpdateIngestionPipelineWizardPanel({
  workspaceId,
  pipeline,
  onAbort,
  onUpdated,
}: UpdateIngestionPipelineWizardPanelProps) {
  const dal = useDAL();
  const { snackbar } = useWorkspace();

  const save = async (
    state: IngestionPipelineWizardState
  ): Promise<boolean> => {
    const request: UpdateIngestionPipelineInput = {
      name: state.name,
      namespace: state.namespace,
      tableName: state.tableName,
      sourceType: state.sourceType,
    };

    if (state.sourceType === IngestionSourceType.INGESTION_SOURCE_TYPE_KAFKA) {
      const kafkaSourceProperties: KafkaSourceProperties =
        buildKafkaSourceProperties(state);
      request.kafkaSourceProperties = kafkaSourceProperties;
    } else {
      const bucketPathPartitionSpec: BucketPathPartitionSpec = {
        separator: state.partitionSpec.separator,
        segments: mapSegments(state.partitionSpec.segments),
      };

      if (state.sourceType === IngestionSourceType.INGESTION_SOURCE_TYPE_S3) {
        const s3SourceProperties: S3SourceProperties = {
          bucket: state.s3Bucket,
          prefix: '',
          region: state.s3Region,
          ownerAccountId: state.s3OwnerAccountId,
          fileFormat: state.fileFormat,
          partitionType: PartitionType.PARTITION_TYPE_BUCKET_PATH,
          bucketPathPartitionSpec,
        };
        request.s3SourceProperties = s3SourceProperties;
      } else {
        const gcsSourceProperties: GcsSourceProperties = {
          bucket: state.gcsBucket,
          prefix: '',
          projectId: state.gcsProjectId,
          location: state.gcsLocation,
          fileFormat: state.fileFormat,
          partitionType: PartitionType.PARTITION_TYPE_BUCKET_PATH,
          bucketPathPartitionSpec,
        };
        request.gcsSourceProperties = gcsSourceProperties;
      }
    }

    try {
      await dal.lakehouse.updateIngestionPipeline(
        workspaceId,
        pipeline.id,
        request
      );
      onUpdated();
      return true;
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to update pipeline';
      snackbar.showErrorMessage(message);
      return false;
    }
  };

  const initialState = pipelineToWizardState(pipeline);

  return (
    <Wizard<IngestionPipelineWizardState>
      onAbort={onAbort}
      hideBackArrow={true}
      onFinish={save}
      flow={[IngestionPipelineCreationFlow]}
      initialState={initialState}
      title='Update ingestion pipeline'
      stepperLabels={getStepperLabels(initialState.sourceType)}
    />
  );
}
